/**
 * @file MeetingNotesTab.tsx
 * @brief Notes page, "Meeting notes" tab: the viewer's own Meeting Notes
 *        recordings as note rows, and the line that says which language
 *        the next one is written in.
 *
 * @details
 * ADR-030. Was MeetingNotesSection (06-10-2026), a list with its own
 * "Start meeting notes" button above My notes. DES 07-10-2026 (web port of
 * the phone's d9397fc, 7f996d8, fa22629): Meeting notes and Personal notes
 * are two pill tabs; this is the first.
 * - Each recording is a row in the notes' timeline look (date sections,
 *   rail, red node as a recorded note's): title or "Meeting notes", date
 *   and time, "Meeting notes · N min · K to-dos" (or the status while it is
 *   made), and the overview as a two-line preview.
 * - Above the rows: "Notes written in <language> · Change", and a hint that
 *   Change also gives the meeting a title. Change opens Setup in Notes mode.
 *   The recording itself starts from the page's dock in one slide
 *   (NotesPage), with no Setup step.
 *
 * Dev only (DES 09-10-2026): the Notes v2 preview meeting's row opens with
 * `?notesPreview=1`, so only that visit shows it as a Notes meeting
 * (company-meetings/notesDoc.ts).
 *
 * The list is loaded by NotesPage (it also hides personal notes that live
 * inside a Notes meeting); this component only draws it.
 *
 * Sharing v2 (DES 21-10-2026): Notes recordings share like Boardroom ones.
 * Each row carries the card's "Shared with / Shared by" line, and the Notes
 * meetings other people shared with the viewer sit in a "Shared with me"
 * column on the right, as on the Notes tab.
 *
 * @references
 * - FRONTEND/src/screens/Notes/NotesListScreen.tsx (MeetingNoteRow)
 * - ./meetingNotesList.ts (kind line, filters)
 * - docs/decisions/ADR-030-meeting-notes-tier.md
 *
 * @author DES
 * @date 06-10-2026
 * @updated 07-10-2026 — a tab of note rows; one-slide start moved to the dock
 */


import { MeetingNotesPen } from "@/components/icons/MeetingNotesPen";
import { Link } from "react-router-dom";
import { InterruptedDraftBanner } from "@/features/employee-boardroom/InterruptedDraftBanner";
import { EmptyState } from "@/components/feedback/EmptyState";
import type { MeetingRow } from "@/features/company-meetings/schemas";
import { isRTLText } from "@/shared/utils/formatters";
import { meetingNoteKindLine } from "./meetingNotesList";
import { NOTES_PREVIEW_QUERY } from "@/features/company-meetings/notesDoc";
import { groupNotes } from "./noteGrouping";
import { formatWhen } from "./noteFormat";
import { MeetingSharingLine } from "@/features/employee-boardroom/MeetingSharingLine";
import { MeetingCardShareMenu } from "@/features/employee-boardroom/MeetingShareItems";
import { meetingShareRights } from "@/features/employee-boardroom/sharingLine";
import { MeetingOwnerLine } from "@/features/company-meetings/components/MineTeamSwitch";
import { TEAM_EMPTY_TITLE } from "@/features/company-meetings/meetingListScope";
import type { ReactNode } from "react";

/** Where a row's node sits; NotesPage's NODE_CENTER_PX, the same rows. */
const NODE_CENTER_PX = 32;

function MeetingNoteRow({
  meeting,
  href,
  first,
  last,
  showOwner = false,
}: {
  meeting: MeetingRow;
  href: string;
  first: boolean;
  last: boolean;
  /** Team list: "Ali's meeting" with the owner's photo. */
  showOwner?: boolean;
}) {
  const title = meeting.title?.trim() || "Meeting notes";
  const kindLine = meetingNoteKindLine(meeting);
  const preview = meeting.meeting_overview?.trim() ?? "";
  return (
    <div className="group relative flex gap-2">
      <div className="relative w-6 shrink-0" aria-hidden>
        {!first ? (
          <span
            className="absolute left-1/2 top-0 w-0.5 -translate-x-1/2 bg-border"
            style={{ height: NODE_CENTER_PX }}
          />
        ) : null}
        {!last ? (
          <span
            className="absolute bottom-0 left-1/2 w-0.5 -translate-x-1/2 bg-border"
            style={{ top: NODE_CENTER_PX }}
          />
        ) : null}
        {/* Red, as a recorded note's: AI-made. */}
        <span
          className="absolute left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-app-page bg-brand"
          style={{ top: NODE_CENTER_PX }}
        />
      </div>
      <Link
        to={href}
        aria-label={`${title}, ${kindLine}`}
        className={`min-w-0 flex-1 rounded-md py-[18px] ${meetingShareRights(meeting.sharing).canSeeAccess ? "pr-10 " : ""}text-left transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-ring active:opacity-60`}
      >
        <span
          dir={isRTLText(title) ? "rtl" : "ltr"}
          className="line-clamp-2 text-xl font-bold leading-7 text-foreground"
        >
          {title}
        </span>
        <span className="mt-1 block text-[15px] font-semibold text-foreground">
          {formatWhen(meeting.started_at)}
        </span>
        {/* Primary text, as a recorded note's "Generated" line. */}
        <span className="mt-1 flex items-center gap-1.5 text-[15px] text-foreground">
          {/* DES 08-10-2026: app icon, was NotebookPen */}
          <MeetingNotesPen className="h-[15px] w-[15px] shrink-0 text-brand" strokeWidth={1.5} />
          <span className="truncate">{kindLine}</span>
        </span>
        {preview ? (
          <span
            dir={isRTLText(preview) ? "rtl" : "ltr"}
            className="mt-1 line-clamp-2 text-[15px] leading-[22px] text-muted-foreground"
          >
            {preview}
          </span>
        ) : null}
        {showOwner ? <MeetingOwnerLine owner={meeting.owner} className="mt-1.5" /> : null}
        {/* Who has it (sharing v2, 21-10-2026). */}
        <MeetingSharingLine meetingId={meeting.id} sharing={meeting.sharing} className="mt-1.5" />
      </Link>
      {/* Share… / Who has access (DES 22-10-2026): the Personal tab rows'
          "⋯", where they put it. */}
      <MeetingCardShareMenu
        meetingId={meeting.id}
        sharing={meeting.sharing}
        title={title}
        className="absolute right-0 top-4 [&>span]:h-9 [&>span]:w-9"
      />
    </div>
  );
}

type Props = {
  /** Already narrowed to Notes meetings and by the page's search / dates. */
  meetings: MeetingRow[];
  loading: boolean;
  /** The page is searching or filtering by date (changes the empty state). */
  filtered: boolean;
  /** "/app" or "/company": where the boardroom pages live. */
  boardroomBase: string;
  /** The notes language's display name. */
  languageLabel: string;
  /** Dev only: the Notes v2 preview meeting, opened with its flag. */
  notesPreviewId?: string | null;
  /** Notes meetings other people shared with the viewer (sharing v2),
   *  already narrowed by the page's search / dates. */
  sharedMeetings?: MeetingRow[];
  sharedLoading?: boolean;
  /** The company admin's Mine | Team switch (renders nothing for others). */
  scopeSwitch?: ReactNode;
  /** Team (22-10-2026): other members' Notes meetings, shown in place of
   *  the two columns, each with its owner. null = Mine. Loading follows
   *  `sharedLoading` (the same list). */
  teamMeetings?: MeetingRow[] | null;
};

function MeetingRowsBySection({
  meetings,
  hrefFor,
  showOwner = false,
}: {
  meetings: MeetingRow[];
  hrefFor: (m: MeetingRow) => string;
  showOwner?: boolean;
}) {
  // The notes' date sections, on the recording's start.
  const sections = groupNotes(
    meetings.map((m) => ({ meeting: m, is_pinned: false, created_at: m.started_at ?? null })),
  );
  return (
    <>
      {sections.map((section) => (
        <section key={section.label}>
          <h2 className="mb-1 mt-4 text-lg font-semibold tracking-[-0.5px] text-foreground/40">
            {section.label}
          </h2>
          {section.notes.map(({ meeting }, i) => (
            <MeetingNoteRow
              key={meeting.id}
              meeting={meeting}
              href={hrefFor(meeting)}
              first={i === 0}
              last={i === section.notes.length - 1}
              showOwner={showOwner}
            />
          ))}
        </section>
      ))}
    </>
  );
}

export function MeetingNotesTab({
  meetings,
  loading,
  filtered,
  boardroomBase,
  languageLabel,
  notesPreviewId = null,
  sharedMeetings = [],
  sharedLoading = false,
  scopeSwitch = null,
  teamMeetings = null,
}: Props) {
  const hrefFor = (meeting: MeetingRow) =>
    `${boardroomBase}/meetings/${meeting.id}${
      notesPreviewId && meeting.id === notesPreviewId ? `?${NOTES_PREVIEW_QUERY}=1` : ""
    }`;
  // Two columns from lg, as the Notes tab: yours left, shared with you right.
  const sharedColumn = (
    <aside className="relative mt-10 min-w-0 lg:mt-0 lg:before:absolute lg:before:-left-6 lg:before:bottom-0 lg:before:top-6 lg:before:w-px lg:before:bg-border/60">
      <h2 className="mt-6 text-[13px] font-bold uppercase tracking-[0.6px] text-muted-foreground">
        Shared with me
      </h2>
      {sharedLoading ? null : sharedMeetings.length === 0 ? (
        <p className="mt-6 text-[15px] leading-[22px] text-muted-foreground">
          {filtered
            ? "No shared meeting notes match your search."
            : "Nothing shared with you yet. When someone shares their meeting notes, they appear here."}
        </p>
      ) : (
        <div className="mt-2">
          <MeetingRowsBySection meetings={sharedMeetings} hrefFor={hrefFor} />
        </div>
      )}
    </aside>
  );

  return (
    <div>
      {/* Half width from lg, as the Personal tab's "My notes" column; the
          empty state below spans the page so it centres over the dock (DES
          07-10-2026: it sat in the left half). */}
      <div className="min-w-0 lg:max-w-[calc(50%-1.5rem)]">
        {scopeSwitch ? <div className="mt-4 flex">{scopeSwitch}</div> : null}
        {/* The one Setup choice Notes has, readable and changeable without a
          Setup step on every start (DES 07-10-2026). */}
        <p className="mt-4 flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
          <span className="truncate">Notes written in {languageLabel}</span>
          <Link
            to={`${boardroomBase}/meetings/new?tier=notes`}
            state={{ tier: "notes" }}
            aria-label="Change notes language or add a title"
            className="shrink-0 rounded-sm font-semibold text-brand hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Change
          </Link>
        </p>
        <p className="mt-1 text-[13px] leading-[18px] text-muted-foreground opacity-80">
          To give the meeting a title or pick another language, tap Change before you start. Without
          a title, one is suggested from what was said.
        </p>
      </div>

      {/* An interrupted Meeting Notes recording is offered here, not on
          Boardrooms (DES 07-10-2026). */}
      <div className="mt-4">
        <InterruptedDraftBanner tier="notes" />
      </div>

      {teamMeetings ? (
        sharedLoading ? null : teamMeetings.length === 0 ? (
          <EmptyState
            shape="note"
            animate={!filtered}
            title={filtered ? "No matches" : TEAM_EMPTY_TITLE}
            description={
              filtered
                ? "No team meeting notes match your search."
                : "Meeting notes your team records show here."
            }
          />
        ) : (
          <div className="mt-2 min-w-0 lg:max-w-[calc(50%-1.5rem)]">
            <MeetingRowsBySection meetings={teamMeetings} hrefFor={hrefFor} showOwner />
          </div>
        )
      ) : loading ? null : meetings.length === 0 && sharedMeetings.length === 0 ? (
        <EmptyState
          shape="note"
          animate={!filtered}
          title={filtered ? "No matches" : "No meeting notes yet"}
          description={
            filtered
              ? "No meeting notes match your search."
              : "Slide below to record a meeting. You get a summary, to-dos and the transcript."
          }
        />
      ) : (
        <div className="lg:grid lg:grid-cols-2 lg:gap-12">
          <div className="mt-2 min-w-0">
            {meetings.length === 0 ? (
              <p className="mt-6 text-[15px] leading-[22px] text-muted-foreground">
                {filtered ? "None of your meeting notes match." : "No meeting notes of your own yet."}
              </p>
            ) : (
              <MeetingRowsBySection meetings={meetings} hrefFor={hrefFor} />
            )}
          </div>
          {sharedColumn}
        </div>
      )}
    </div>
  );
}
